import { ClipLoader } from "react-spinners";
import LoadingSkeletonImageDynamic from "../../../components/LoadingSkeletonImageDynamic";
import { useTheme } from "../../../context/ThemeProvider";
import useApiPost from "../../../hooks/PostData";
import { useBlockUserList } from "../../../store/api/useBlockUserList";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { useState } from "react";
import TextTranslate from "../../../utils/TextTranslate";
import { BlockUserRes } from "../../../types/ResType";
import ShowSavedName from "../../../components/ShowSavedName";
import { updateCurrentConversation } from "../../../store/Slices/CurrentConversationSlice";
import SearchNotFoundAnimation from "../../../components/SearchNotFoundAnimation";

const groupClass =
  "overflow-hidden rounded-2xl bg-secondary shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06]";
const divide =
  "[&>*+*]:border-t [&>*+*]:border-black/[0.06] dark:[&>*+*]:border-white/[0.08]";

export default function BlockUserList() {
  const [selectedConversationId, setSelectedConversationId] = useState(0);
  // @ts-ignore
  const { theme } = useTheme();
  const { data, isLoading, refetch } = useBlockUserList();
  const OnlineUserList = useAppSelector((state) => state.OnlineUserList);
  const dispatch = useAppDispatch();
  const { loading, postData } = useApiPost();

  async function unblockUser({ user_id }: { user_id: number }) {
    const blockUserResponse: BlockUserRes = await postData(
      "block/block-unblock",
      {
        user_id: user_id.toString(),
      },
    );
    dispatch(
      updateCurrentConversation({
        is_group_blocked: blockUserResponse.data.is_blocked,
      }),
    );
    refetch();
  }

  const records = data?.data?.Records ?? [];

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-y-auto px-4 py-4 pb-8">
      {isLoading ? (
        <div className="grid flex-1 place-content-center">
          <ClipLoader size={23} color={theme == "dark" ? "white" : "black"} />
        </div>
      ) : records.length === 0 ? (
        <div className="grid flex-1 place-content-center gap-4 text-center text-lightText">
          <SearchNotFoundAnimation />
          <div>
            <TextTranslate text="No blocked account" />
          </div>
        </div>
      ) : (
        <div className={`${groupClass} ${divide}`}>
          {records.map((e) => {
            const userId = e?.blocked.user_id;
            const busy =
              (loading || isLoading) && selectedConversationId == userId;
            return (
              <div
                key={userId}
                className="flex items-center gap-3 px-3 py-3"
              >
                <div className="relative h-12 w-12 shrink-0">
                  <LoadingSkeletonImageDynamic
                    radius=""
                    className="h-12 w-12 rounded-full object-cover"
                    image_height="100%"
                    image_url={e?.blocked.profile_pic}
                    image_width=""
                  />
                  {OnlineUserList.onlineUsers.find(
                    (user) => user.user_id == e.user_id,
                  ) && (
                    <img
                      className="absolute bottom-0 right-0 z-30 h-3.5 w-3.5"
                      src="/Home/Online_Green_dot.png"
                      alt=""
                    />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="truncate text-[16px] font-semibold capitalize text-darkText">
                    <ShowSavedName
                      user_id={userId.toString()}
                      name={e?.blocked.full_name}
                    />
                  </div>
                  {e?.blocked.user_name ? (
                    <div className="mt-0.5 truncate text-[13px] text-lightText">
                      @{e.blocked.user_name}
                    </div>
                  ) : null}
                </div>

                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setSelectedConversationId(userId);
                    void unblockUser({ user_id: userId });
                  }}
                  className="shrink-0 rounded-full bg-black/[0.06] px-3.5 py-1.5 text-[14px] font-semibold text-themeColor transition active:scale-95 disabled:opacity-50 dark:bg-white/10"
                >
                  {busy ? (
                    <ClipLoader
                      size={14}
                      color={theme == "dark" ? "white" : "black"}
                    />
                  ) : (
                    <TextTranslate text="Unblock" />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
